import React, { useEffect, useRef } from "react";
import Typed from 'typed.js';
import { Link } from "react-router-dom";
import { FiCheck, FiClock, FiUsers, FiActivity, FiTrendingUp, FiAward, FiShield, FiTarget, FiArrowRight } from "react-icons/fi";
import SEO from "../../common/SEO";
import HeaderOne from '../../common/header/HeaderOne';
import HeaderTopBar from '../../common/header/HeaderTopBar';
import Copyright from '../../common/footer/Copyright';
import SectionTitle from "../sectionTitle/SectionTitle";
import ERPSolutionsPricing from "../pricing/ERPSolutionsPricing";
import FooterTwo from "../../common/footer/FooterTwo";
import Separator from "../separator/Separator";
import ScrollAnimation from "react-animate-on-scroll";

// Extracted Static Data
const AgendaData = [
    { icon: <FiActivity />, title: "Marketing", description: "Share campaign and lead data." },
    { icon: <FiUsers />, title: "Operations", description: "Coordinate inventory and supply." },
    { icon: <FiTarget />, title: "Sales", description: "Sync orders and customer data." },
    { icon: <FiShield />, title: "Finance", description: "Manage invoices and reporting." }
];

const WhyJourneyList = [
    { icon: <FiCheck />, text: "Streamlined workflows and processes" },
    { icon: <FiCheck />, text: "Real time data analytics and insights" },
    { icon: <FiCheck />, text: "Enhanced team collaboration" }
];

const ERPCoreComponents = [
    { title: "Finance", desc: "Accounting & Budget" },
    { title: "HRM", desc: "People & Payroll" },
    { title: "Inventory", desc: "Stock & Supply" },
    { title: "Sales", desc: "CRM & Orders" }
];

const BenefitsData = [
    { title: "Sales Teams", items: ["Lead tracking", "Pipeline manage", "Customer insights"] },
    { title: "HR Teams", items: ["Employee manage", "Payroll processing", "Performance track"] },
    { title: "Operations", items: ["Inventory control", "Order manage", "Supply chain"] },
    { title: "Finance", items: ["Financial reports", "Budgeting", "Expense tracking"] }
];

const TimelineData = [
    { step: "01", title: "Discovery & Setup", desc: "We analyze your needs and configure your ERP instance" },
    { step: "02", title: "Data Migration", desc: "Import your existing data securely into the new system" },
    { step: "03", title: "Team Training", desc: "Comprehensive training for your entire team" },
    { step: "04", title: "Go Live", desc: "Launch your new ERP system with full support" },
    { step: "05", title: "Optimization", desc: "Continuous improvement and feature updates" },
    { step: "06", title: "Support & Maintenance", desc: "Ongoing support and maintenance to ensure system stability" }
];

const CRMFeaturesList = [
    "Lead capture and qualification",
    "Sales pipeline management",
    "Customer communication tracking",
    "Performance analytics and reporting"
];

const MoreThanToolFeatures = [
    { icon: <FiTrendingUp />, text: "Scalable Growth" },
    { icon: <FiAward />, text: "Industry Leading" },
    { icon: <FiShield />, text: "Enterprise Security" },
    { icon: <FiTarget />, text: "Goal Focused" }
];

const FAQData = [
    { q: "How long does implementation take?", a: "Implementation typically takes 2–6 weeks depending on your business size and complexity." },
    { q: "Is training included with the package?", a: "Yes! All packages include comprehensive training and onboarding support." },
    { q: "Can we customize the ERP for our needs?", a: "Absolutely! Our ERP is highly customizable to fit your unique business requirements." },
    { q: "What kind of support do you offer?", a: "We offer 24/7 support via chat, email, and phone for all paid packages." }
];

const ERPSolutions = () => {
    const el = useRef(null);

    useEffect(() => {
        const typed = new Typed(el.current, {
            strings: [
                "Transform Your ERP & HRM Operations.",
                "Streamline Your Business Workflows."
            ],
            typeSpeed: 80,
            backSpeed: 60,
            backDelay: 2000,
            startDelay: 500,
            loop: true
        });

        return () => {
            typed.destroy();
        };
    }, []);

    return (
        <>
            <style>{`
                    .hero-title-container {
                        height: 300px;
                        margin-bottom: 5px;
                    }
                    @media (max-width: 768px) {
                        .hero-title-container {
                            height: 400px;
                        }
                    }
                    @media (max-width: 480px) {
                        .hero-title-container {
                            height: 450px;
                        }
                    }
                .erp-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    background: linear-gradient(90deg, rgba(101, 73, 213, 0.2), rgba(5, 157, 255, 0.2));
                    padding: 10px 24px;
                    border-radius: 30px;
                    border: 1px solid rgba(101, 73, 213, 0.3);
                    margin-bottom: 10px;
                    margin-top: 20px;
                }
                .erp-hero-badge span {
                    font-size: 14px;
                    font-weight: 700;
                    background: linear-gradient(90deg, #6549D5, #059DFF);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .slider-area .thumbnail img {
                    border-radius: 30px;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15);
                    border: 1px solid rgba(255,255,255,0.05);
                }
                .slider-area .title {
                    min-height: auto !important;
                    margin-top: 0 !important;
                    margin-bottom: 15px !important;
                }
                .slider-area .description {
                    margin-bottom: 25px !important;
                }
                .slider-area .inner {
                    margin-top: 0 !important;
                }
                .slider-area.height-750 {
                    min-height: auto !important;
                    height: auto !important;
                    padding: 100px 0 40px 0 !important;
                }
                .main-content {
                    padding-top: 0 !important;
                }
                .erp-card {
                    background: linear-gradient(135deg, rgba(15, 15, 17, 0.95), rgba(26, 26, 26, 0.95));
                    border-radius: 25px;
                    padding: 45px 35px;
                    height: 100%;
                    border: 1px solid rgba(101, 73, 213, 0.15);
                    transition: all 0.3s ease;
                }
                .erp-card:hover {
                    transform: translateY(-10px);
                    border-color: rgba(101, 73, 213, 0.3);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 40px rgba(101, 73, 213, 0.1);
                }
                .erp-icon-box {
                    width: 60px;
                    height: 60px;
                    border-radius: 15px;
                    background: linear-gradient(135deg, rgba(101,73,213,0.2), rgba(5,157,255,0.2));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 26px;
                    color: #6549D5;
                    margin-bottom: 20px;
                }
                .erp-card-title {
                    color: #fff;
                    font-size: 20px;
                    font-weight: 700;
                    margin-bottom: 20px;
                }
                .erp-card-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
                .erp-card-list-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #acacac;
                    margin-bottom: 10px;
                }
                .erp-card-list-icon {
                    color: #4CAF50;
                }
                .erp-section {
                    background: linear-gradient(135deg, rgba(101,73,213,0.03), rgba(5,157,255,0.03));
                    border-radius: 40px;
                    padding: 80px 30px;
                    margin: 0 20px;
                }
                .erp-section-dark {
                    background: linear-gradient(135deg, rgba(15, 15, 17, 0.8), rgba(26, 26, 26, 0.9));
                    border-radius: 40px;
                    padding: 60px 30px;
                    margin: 0 20px;
                }
                .erp-section-image {
                    width: 100%;
                    border-radius: 25px;
                }
                .erp-section-title {
                    color: #fff;
                    font-size: 36px;
                    font-weight: 700;
                    margin-bottom: 20px;
                }
                .erp-section-description {
                    color: #acacac;
                    font-size: 16px;
                    line-height: 1.7;
                    margin-bottom: 25px;
                }
                .erp-section-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }
                .erp-section-list-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    color: #acacac;
                }
                .erp-section-list-icon {
                    color: #4CAF50;
                }
                .erp-circle-diagram {
                    width: 200px;
                    height: 200px;
                    border-radius: 50%;
                    border: 3px solid #6549D5;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    position: relative;
                }
                .erp-circle-text h4 {
                    color: #fff;
                    margin: 0;
                }
                .erp-circle-item {
                    position: absolute;
                    width: 120px;
                    text-align: center;
                }
                .erp-circle-item:nth-child(2) { top: -30px; left: 50%; transform: translateX(-50%); }
                .erp-circle-item:nth-child(3) { right: -40px; top: 50%; transform: translateY(-50%); }
                .erp-circle-item:nth-child(4) { bottom: -30px; left: 50%; transform: translateX(-50%); }
                .erp-circle-item:nth-child(5) { left: -40px; top: 50%; transform: translateY(-50%); }
                .erp-circle-item-title {
                    color: #6549D5;
                    font-weight: 700;
                }
                .erp-circle-item-desc {
                    color: #acacac;
                    font-size: 12px;
                }
                .erp-timeline-item {
                    position: relative;
                    padding-left: 40px;
                }
                .erp-timeline-dot {
                    position: absolute;
                    left: 0;
                    top: 5px;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6549D5, #059DFF);
                }
                .erp-timeline-step-badge {
                    display: inline-block;
                    background: linear-gradient(135deg, rgba(101,73,213,0.2), rgba(5,157,255,0.2));
                    padding: 5px 15px;
                    border-radius: 10px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #6549D5;
                    margin-bottom: 10px;
                }
                .erp-timeline-title {
                    color: #fff;
                    font-size: 22px;
                    font-weight: 700;
                    margin-bottom: 8px;
                }
                .erp-timeline-desc {
                    color: #acacac;
                    font-size: 15px;
                    line-height: 1.7;
                }
                .erp-features-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }
                .erp-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    color: #acacac;
                }
                .erp-feature-icon {
                    color: #6549D5;
                    font-size: 24px;
                }
                .erp-feature-text {
                    font-weight: 600;
                }
                .erp-faq-item {
                    border: 1px solid rgba(101,73,213,0.2);
                    border-radius: 15px;
                    padding: 20px 25px;
                    margin-bottom: 15px;
                    background: rgba(15, 15, 17, 0.8);
                }
                .erp-faq-question {
                    color: #fff;
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 10px;
                }
                .erp-faq-answer {
                    color: #acacac;
                    font-size: 15px;
                    line-height: 1.6;
                    margin-bottom: 0;
                }
                .erp-intro-section {
                    background: linear-gradient(135deg, rgba(15, 15, 17, 0.95), rgba(26, 26, 26, 0.95));
                    border-radius: 30px;
                    padding: 60px 30px;
                    margin: 0 20px;
                }
                .erp-intro-title {
                    background: linear-gradient(90deg, #6549D5, #E33FA1, #6549D5);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    font-size: 42px;
                    font-weight: 800;
                    margin-bottom: 20px;
                    line-height: 1.2;
                }
                .erp-intro-desc {
                    color: #acacac;
                    font-size: 18px;
                    margin-bottom: 40px;
                    max-width: 900px;
                }
                .erp-intro-card {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    padding: 25px;
                    transition: all 0.3s ease;
                }
                .erp-intro-card:hover {
                    transform: translateY(-5px);
                }
                .erp-intro-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 15px;
                    background: linear-gradient(135deg, rgba(101, 73, 213, 0.15), rgba(227, 63, 161, 0.15));
                    border: 2px solid rgba(227, 63, 161, 0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .erp-intro-icon {
                    font-size: 28px;
                    color: #E33FA1;
                }
                .erp-intro-card-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #fff;
                    margin-bottom: 5px;
                }
                .erp-intro-card-desc {
                    color: #acacac;
                    font-size: 16px;
                }
                .erp-hub-section {
                    padding: 80px 0;
                }
                .erp-hub-wrapper {
                    position: relative;
                    max-width: 800px;
                    margin: 60px auto;
                }
                .erp-hub-core {
                    width: 260px;
                    height: 260px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3a247c, #2a1a5c);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    margin: 0 auto;
                    position: relative;
                    z-index: 2;
                    padding: 30px;
                }
                .erp-hub-core h4 {
                    color: #fff;
                    font-size: 24px;
                    font-weight: 800;
                    margin-bottom: 10px;
                }
                .erp-hub-core p {
                    color: rgba(255,255,255,0.8);
                    font-size: 15px;
                    line-height: 1.5;
                    margin: 0;
                }
                .erp-hub-ring {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-radius: 50%;
                    border: 2px solid rgba(101,73,213,0.2);
                }
                .erp-hub-ring-1 {
                    width: 420px;
                    height: 420px;
                }
                .erp-hub-ring-2 {
                    width: 560px;
                    height: 560px;
                }
                .erp-hub-card {
                    position: absolute;
                    background: linear-gradient(135deg, #4a2e9e, #6549D5);
                    padding: 20px 30px;
                    border-radius: 20px;
                    text-align: center;
                    min-width: 180px;
                    z-index: 3;
                    transition: all 0.3s ease;
                }
                .erp-hub-card:hover {
                    transform: scale(1.12);
                    box-shadow: 0 20px 50px rgba(101,73,213,0.5);
                }
                .erp-hub-core:hover {
                    transform: scale(1.08);
                    box-shadow: 0 0 40px rgba(101,73,213,0.4);
                }
                .erp-hub-card h4 {
                    color: #fff;
                    font-size: 20px;
                    font-weight: 700;
                    margin-bottom: 8px;
                }
                .erp-hub-card p {
                    color: rgba(255,255,255,0.9);
                    font-size: 14px;
                    margin: 0;
                }
                .erp-hub-card-top {
                    top: -20px;
                    left: 50%;
                    transform: translateX(-50%);
                }
                .erp-hub-card-right {
                    right: -20px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                .erp-hub-card-bottom {
                    bottom: -20px;
                    left: 50%;
                    transform: translateX(-50%);
                }
                .erp-hub-card-left {
                    left: -20px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                @media (max-width: 992px) {
                    .erp-hub-wrapper {
                        max-width: 500px;
                    }
                    .erp-hub-core {
                        width: 200px;
                        height: 200px;
                        padding: 20px;
                    }
                    .erp-hub-ring-1 {
                        width: 350px;
                        height: 350px;
                    }
                    .erp-hub-ring-2 {
                        width: 480px;
                        height: 480px;
                    }
                    .erp-hub-card {
                        min-width: 150px;
                        padding: 15px 20px;
                    }
                }
                @media (max-width: 768px) {
                    .erp-hub-wrapper {
                        max-width: 100%;
                        margin-top: 40px;
                    }
                    .erp-hub-ring {
                        display: none;
                    }
                    .erp-hub-card {
                        position: relative;
                        left: auto;
                        right: auto;
                        top: auto;
                        bottom: auto;
                        transform: none;
                        margin-bottom: 20px;
                        width: 100%;
                    }
                    .erp-hub-core {
                        margin-bottom: 40px;
                    }
                }
                /* LIGHT MODE */
                body.light .erp-hub-core {
                    background: linear-gradient(135deg, #6549D5, #4a2e9e);
                }
                body.light .erp-hub-card {
                    background: linear-gradient(135deg, #f0e8ff, #e0d0ff);
                }
                body.light .erp-hub-card h4 {
                    color: #3a247c !important;
                }
                body.light .erp-hub-card p {
                    color: #5a408c !important;
                }
                body.light .erp-hub-ring {
                    border-color: rgba(101,73,213,0.3);
                }
                /* LIGHT MODE */
                body.light .erp-card {
                    background: linear-gradient(135deg, #f5f8fa, #e9f8fe);
                }
                body.light .erp-card h4,
                body.light .erp-card h2,
                body.light .erp-section-dark h2,
                body.light .erp-section-dark h4,
                body.light .erp-faq-question {
                    color: #181818 !important;
                }
                body.light .erp-card p,
                body.light .erp-card li,
                body.light .erp-section-dark p,
                body.light .erp-section-dark li,
                body.light .erp-faq-answer,
                body.light .erp-card-list-item,
                body.light .erp-section-list-item {
                    color: #65676b !important;
                }
                body.light .erp-section-dark {
                    background: linear-gradient(135deg, rgba(245,248,250,0.95), rgba(233,248,254,0.95));
                }
                body.light .erp-faq-item {
                    background: rgba(245, 248, 250, 0.95);
                }
                body.light .erp-stat-number {
                    color: #6549D5;
                }
                body.light .erp-stat-label {
                    color: #65676b;
                }
                body.light .erp-circle-text h4,
                body.light .erp-circle-item-title {
                    color: #181818 !important;
                }
                body.light .erp-timeline-title {
                    color: #181818 !important;
                }
                body.light .erp-timeline-desc {
                    color: #65676b !important;
                }
                body.light .erp-hero-description {
                    color: #65676b;
                }
                body.light .erp-communication-text {
                    color: #65676b;
                }
                body.light .erp-intro-section {
                    background: linear-gradient(135deg, rgba(245, 248, 250, 0.95), rgba(233, 248, 254, 0.95));
                }
                body.light .erp-intro-card-title {
                    color: #181818 !important;
                }
                body.light .erp-intro-desc,
                body.light .erp-intro-card-desc {
                    color: #65676b !important;
                }
            `}</style>
            <SEO title="ERP Solutions || WhatsOn Agency Services" />
            <main className="page-wrapper theme-shape">
                <HeaderTopBar />
                <HeaderOne btnStyle="round btn-icon" HeaderSTyle="header-not-transparent" />

                {/* Hero Section - AI Vibe */}
                <div className="slider-area slider-style-1 bg-transparent variation-2" style={{ paddingTop: '0px', paddingBottom: '30px' }}>
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-8 order-2 order-lg-1 mt_md--40 mt_sm--40">
                                <div className="inner text-start" style={{ paddingTop: '0px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                        <div className="erp-hero-badge">
                                            <FiActivity />
                                            <span>NEXT-GEN ERP</span>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={150}>
                                        <div className="hero-title-container">
                                            <h2 className="title theme-gradient display-two" style={{ marginBottom: '0', height: 'auto' }}>
                                                We {" "}
                                                <span ref={el} className="theme-gradient"></span>
                                            </h2>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={300}>
                                        <p className="description" style={{ marginBottom: '', marginTop: '50px' }}>
                                            Empower your business with intelligent ERP & HRM solutions that streamline operations, enhance productivity, and drive growth.
                                        </p>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={450}>
                                        <div className="button-group" aria-label="Get Contact">
                                            <Link
                                                className="btn-default btn-medium round btn-icon"
                                                to="/contact"
                                            >
                                                Contact Us
                                                <i className="icon">
                                                    <FiArrowRight />
                                                </i>
                                            </Link>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            </div>
                            <div className="col-lg-4 order-1 order-lg-2">
                                <ScrollAnimation animateIn="fadeInRight" animateOnce={true} delay={200}>
                                    <div className="thumbnail">
                                        <img
                                            src="./images/bg/erpsolutions.png"
                                            alt="ERP Solutions"
                                            style={{ 
                                                borderRadius: '30px', 
                                                boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                border: '1px solid rgba(255,255,255,0.05)'
                                            }}
                                        />
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />

                    {/* Benefits Section */}
                    <div className="rn-section-gap">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-12">
                                    <SectionTitle
                                        textAlign="text-center"
                                        radiusRounded=""
                                        subtitle="Introduction to Our New ERP System"
                                        title="Who Benefits from Identifying Your Opportunities"
                                        description="Every team in your organization benefits"
                                    />
                                </div>
                            </div>
                            <div className="row mt--40">
                                {BenefitsData.map((item, index) => (
                                    <div key={index} className="col-lg-3 col-md-6 col-sm-12 mt--30">
                                        <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 80}>
                                            <div className="erp-card">
                                                <h4 className="erp-card-title">{item.title}</h4>
                                                <ul className="erp-card-list">
                                                    {item.items.map((listItem, listIndex) => (
                                                        <li key={listIndex} className="erp-card-list-item">
                                                            <FiCheck className="erp-card-list-icon" />
                                                            {listItem}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </ScrollAnimation>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Timeline Section */}
                    <div className="erp-section">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-12">
                                    <SectionTitle
                                        textAlign="text-center"
                                        radiusRounded=""
                                        subtitle="PROCESS"
                                        title="Your Direct Route Navigating the ERP System"
                                        description="Simple steps to get up and running"
                                    />
                                </div>
                            </div>
                            <div className="row mt--50">
                                <div className="col-lg-6 col-md-12 mb--30">
                                    {TimelineData.slice(0, 3).map((item, index) => (
                                        <ScrollAnimation key={index} animateIn="fadeInUp" animateOnce={true} delay={index * 80}>
                                            <div className="erp-timeline-item mb--30">
                                                <div className="erp-timeline-dot"></div>
                                                <div>
                                                    <div className="erp-timeline-step-badge">Step {item.step}</div>
                                                    <h4 className="erp-timeline-title">{item.title}</h4>
                                                    <p className="erp-timeline-desc">{item.desc}</p>
                                                </div>
                                            </div>
                                        </ScrollAnimation>
                                    ))}
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    {TimelineData.slice(3, 6).map((item, index) => (
                                        <ScrollAnimation key={index + 3} animateIn="fadeInUp" animateOnce={true} delay={(index + 3) * 80}>
                                            <div className="erp-timeline-item mb--30">
                                                <div className="erp-timeline-dot"></div>
                                                <div>
                                                    <div className="erp-timeline-step-badge">Step {item.step}</div>
                                                    <h4 className="erp-timeline-title">{item.title}</h4>
                                                    <p className="erp-timeline-desc">{item.desc}</p>
                                                </div>
                                            </div>
                                        </ScrollAnimation>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* CRM Section */}
                    <div className="erp-section-dark">
                        <div className="container">
                            <div className="row align-items-center">
                                <div className="col-lg-4 col-md-12 mb-4 mb-lg-0 order-2 order-lg-1">
                                    <ScrollAnimation animateIn="fadeInLeft" animateOnce={true}>
                                        <div className="thumbnail">
                                            <img 
                                                src="/images/bg/crm.jpg" 
                                                alt="CRM" 
                                                style={{ 
                                                    borderRadius: '30px', 
                                                    boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    width: '100%'
                                                }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    </ScrollAnimation>
                                </div>
                                <div className="col-lg-8 col-md-12 order-1 order-lg-2">
                                    <ScrollAnimation animateIn="fadeInRight" animateOnce={true}>
                                        <h2 className="erp-section-title">CRM for Sales Lead Tracking, Nurturing Relationships</h2>
                                        <p className="erp-section-description">
                                            Build stronger customer relationships with our integrated CRM tools. Track leads, manage opportunities, and close deals faster.
                                        </p>
                                        <ul className="erp-section-list">
                                            {CRMFeaturesList.map((listItem, index) => (
                                                <li key={index} className="erp-section-list-item">
                                                    <FiCheck className="erp-section-list-icon" style={{ flexShrink: '0' }} />
                                                    {listItem}
                                                </li>
                                            ))}
                                        </ul>
                                    </ScrollAnimation>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* ERP as Tool Section */}
                    <div className="rn-section-gap">
                        <div className="container">
                            <div className="row align-items-center">
                                <div className="col-lg-8 col-md-12 mb-4 mb-lg-0">
                                    <ScrollAnimation animateIn="fadeInLeft" animateOnce={true}>
                                        <h2 className="erp-section-title">Why Your ERP is More Than a Tool</h2>
                                        <p className="erp-section-description">
                                            It's your strategic partner for growth, helping you make data-driven decisions and scale your business efficiently.
                                        </p>
                                        <div className="erp-features-grid">
                                            {MoreThanToolFeatures.map((item, index) => (
                                                <div key={index} className="erp-feature-item">
                                                    <span className="erp-feature-icon">{item.icon}</span>
                                                    <span className="erp-feature-text">{item.text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollAnimation>
                                </div>
                                <div className="col-lg-4 col-md-12">
                                    <ScrollAnimation animateIn="fadeInRight" animateOnce={true}>
                                        <div className="thumbnail">
                                            <img 
                                                src="/images/bg/erp.jpg" 
                                                alt="ERP Tool" 
                                                style={{ 
                                                    borderRadius: '30px', 
                                                    boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    width: '100%'
                                                }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* FAQ Section */}
                    <div className="erp-section-dark">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-12">
                                    <SectionTitle
                                        textAlign="text-center"
                                        radiusRounded=""
                                        subtitle="FAQ"
                                        title="Common Questions Your Sales Toolkit"
                                        description="Answers to frequently asked questions"
                                    />
                                </div>
                            </div>
                            <div className="row mt--40">
                                <div className="col-lg-8 offset-lg-2">
                                    {FAQData.map((faq, index) => (
                                        <ScrollAnimation key={index} animateIn="fadeInUp" animateOnce={true} delay={index * 80}>
                                            <div className="erp-faq-item">
                                                <h4 className="erp-faq-question">{faq.q}</h4>
                                                <p className="erp-faq-answer">{faq.a}</p>
                                            </div>
                                        </ScrollAnimation>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Pricing Section (existing) */}
                    <div className="rwt-pricing-area rn-section-gap">
                        <div className="container">
                            <div className="row mb--40 mb_sm--0">
                                <div className="col-lg-12">
                                    <SectionTitle
                                        textAlign="text-center"
                                        radiusRounded=""
                                        subtitle="Pricing"
                                        title="Choose your prefered WhatsOn ERP Solutions pricing plan"
                                        description=""
                                    />
                                </div>
                            </div>
                            <ERPSolutionsPricing />
                        </div>
                    </div>
                    <FooterTwo />
                    <Copyright />
            </main>
        </>
    );
};

export default ERPSolutions;
